import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useUrlFilters } from "../../hooks/useUrlFilters";
import { ComponentPage } from "../../constants/constants";
import { AddProject } from "./sections";
import RecentWorkUnits from "../../features/WorkUnits/RecentWorkUnits/RecentWorkUnits";
import ListWorkUnits from "../../features/WorkUnits/ListWorkUnits/ListWorkUnits";
import { useOrganizationService } from "../../services";
import { useJwt } from "../../helpers/common";
import { useSideNavContext } from "../../context/SideNavContext";
import { resetTimelineState } from "../../redux/slices/timeline";
import { workUnitsTableHeaders } from "../../constants/constants";
import WorkUnitsFilters from "./sections/WorkUnitsFilters";
import { useIntegrationStatus } from "../../hooks/useIntegrationStatus";
import { resetRoadmap, setSingleRoadmap } from "../../redux/slices/roadmap";
import { clearWorkItemsData, setWorkItemsSearchTerm } from "../../redux/slices/organization";
import { motion, AnimatePresence } from "framer-motion";
import { HiArrowLeft, HiInformationCircle } from "react-icons/hi";
import CustomTooltip from "../../components/common/CustomTooltip";
import { OnboardingProjects } from "../../features/OnboardingGuide/ProjectGuide/ProjectGuide";

/**
 * Projects Page Component
 *
 * Features:
 * - Static Header
 * - Scrollable Content
 * - Filters
 */
const Projects = () => {
  const jwtData: any = useJwt();
  const { project_id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isOpenOnSmallScreens } = useSideNavContext();
  const { isIntegrationActive } = useIntegrationStatus();
  const {
    fetchRoadmapsAndProjects,
    fetchMoreRoadmapsAndProjects,
    fetchOrgSchedule,
    fetchPinnedItems,
  } = useOrganizationService();
  const org_id = jwtData.org_id || "";

  // Redux state
  const orgData = useSelector((state: any) => state.organization);
  const {
    workItemsLoading: orgLoading = false,
    workItems: all_project_data = [],
    workItemsLoadingMore: loadingMore = false,
    workItemsHasMore: hasMore = false,
    workItemsTotalItems: totalItems = 0,
    workItemsPage: currentPage = 1,
    workItemsTotalPages: totalPages = 1,
    // Filter state
    workItemsSearchTerm = "",
    workItemsSearchType = "all",
    workItemsSortBy = "modified_at_desc",
    workItemsSourceFilter = "",
  } = orgData;

  const { loading } = useSelector((state: any) => state.projects);
  const { show_cost: showCost } = useSelector(
    (state: any) => state.organization.orgDetails || {},
  );

  // Component state
  const [showModal, setShowModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState({
    project_name: "",
    project_id: "",
  });
  const [showTable, setShowTable] = useState(true);

  // Initial loading state for pagination text
  const [initialLoading, setInitialLoading] = useState(
    () => !all_project_data || all_project_data.length === 0,
  );

  useEffect(() => {
    if (!org_id) return;
    if (all_project_data && all_project_data.length > 0) {
      setInitialLoading(false);
      return;
    }
    if (!orgLoading && (!all_project_data || all_project_data.length === 0)) {
      const timer = setTimeout(() => {
        setInitialLoading(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [orgLoading, all_project_data, org_id]);

  // Recent Projects Data & Loading State
  const { recentProjects, recentProjectsLoading } = orgData;
  const [recentInitialLoading, setRecentInitialLoading] = useState(
    () => !recentProjects || recentProjects.length === 0,
  );

  useEffect(() => {
    if (
      recentProjectsLoading ||
      (recentProjects && recentProjects.length > 0)
    ) {
      setRecentInitialLoading(false);
    }
  }, [recentProjectsLoading, recentProjects]);

  // Lifted state for filters - using centralized URL filters hook
  const { filters, setFilter, setFilters } = useUrlFilters([
    { urlKey: 'type', storageKey: 'projects_searchType', defaultValue: workItemsSearchType || 'all' },
    { urlKey: 'status', storageKey: 'projects_statusFilter', defaultValue: '', multi: true },
    { urlKey: 'source', storageKey: 'projects_sourceFilter', defaultValue: workItemsSourceFilter || '' },
    { urlKey: 'sort', storageKey: 'projects_sortBy', defaultValue: workItemsSortBy || 'modified_at_desc' },
    { urlKey: 'layout', storageKey: 'projectsViewType', defaultValue: 'list' },
    { urlKey: 'pinned', storageKey: 'projects_pinnedFilter', defaultValue: 'false' },
    { urlKey: 'quarter', storageKey: 'projects_quarterFilter', defaultValue: '', multi: true },
  ], ComponentPage.WORK_ITEMS);

  const searchType = (filters.type as "all" | "roadmap" | "project" | "epic" | "feature") || "all";
  const statusFilter = (filters.status as string[]) || [];
  const sourceFilter = (filters.source as string) || "";
  const sortBy = (filters.sort as "name_asc" | "name_desc" | "created_at_asc" | "created_at_desc" | "modified_at_asc" | "modified_at_desc") || "modified_at_desc";
  const viewType = (filters.layout as "list" | "grid") || "list";
  const pinnedFilter = filters.pinned === "true";
  const quarterFilter = (filters.quarter as string[]) || [];

  // Setter wrappers for WorkUnitsFilters compatibility
  const setSearchType = (val: any) => setFilter('type', val);
  const setStatusFilter = (val: any) => setFilter('status', val);
  const setSourceFilter = (val: any) => setFilter('source', val);
  const setSortBy = (val: any) => setFilter('sort', val);
  const setViewType = (val: any) => setFilter('layout', val);
  const setPinnedFilter = (val: any) => setFilter('pinned', String(val));
  const setQuarterFilter = (val: any) => setFilter('quarter', val);

  // Determine if any filter is active beyond defaults
  const hasActiveFilters = useMemo(() => {
    if (searchType !== 'all') return true;
    if (statusFilter && statusFilter.length > 0) return true;
    if (sourceFilter) return true;
    if (pinnedFilter) return true;
    if (quarterFilter && quarterFilter.length > 0) return true;
    if (workItemsSearchTerm) return true;
    return false;
  }, [searchType, statusFilter, sourceFilter, pinnedFilter, quarterFilter, workItemsSearchTerm]);

  const handleClearFilters = useCallback(() => {
    setFilters({
      type: 'all',
      status: [],
      source: '',
      sort: 'modified_at_desc',
      pinned: 'false',
      quarter: []
    });
    
    dispatch(clearWorkItemsData());
    dispatch(setWorkItemsSearchTerm(''));
    // Fire event to reset local search input state in WorkUnitsFilters
    window.dispatchEvent(new Event("reset-workunits-filters"));
  }, [setFilters, dispatch]);

  // ========================================
  // UNIFIED DATA FETCHING
  // ========================================
  const lastFetchRef = useRef("");

  useEffect(() => {
    if (org_id) {
      const params = JSON.stringify({
        searchType,
        sortBy,
        sourceFilter,
        pinnedFilter,
        quarterFilter,
        workItemsSearchTerm,
      });

      if (lastFetchRef.current !== params) {
        lastFetchRef.current = params;
        dispatch(clearWorkItemsData());
        fetchRoadmapsAndProjects(
          true,
          undefined,
          workItemsSearchTerm,
          searchType,
          sortBy,
          sourceFilter,
          quarterFilter,
          pinnedFilter
        );
      }
    }
  }, [
    org_id,
    searchType,
    sortBy,
    sourceFilter,
    pinnedFilter,
    quarterFilter,
    workItemsSearchTerm,
    dispatch,
    fetchRoadmapsAndProjects,
  ]);

  // Compute filteredProjectData synchronously to avoid UI flash
  const filteredProjectData = useMemo(() => {
    if (!statusFilter || statusFilter.length === 0) return all_project_data;
    return all_project_data.filter((project: any) => statusFilter.includes(project.status));
  }, [all_project_data, statusFilter]);

  // Refs
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const gridScrollRef = useRef<HTMLDivElement>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const filterSectionRef = useRef<HTMLDivElement>(null);

  // ========================================
  // COLUMN VISIBILITY
  // ========================================
  const anyIntegrationActive = useMemo(() => {
    return ["azure", "jira", "asana", "monday"].some((k) =>
      isIntegrationActive(k),
    );
  }, [isIntegrationActive]);

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    () => {
      const savedSettings = localStorage.getItem("projectListColumnVisibility");
      if (savedSettings) {
        try {
          return JSON.parse(savedSettings);
        } catch (error) {
          console.error("Failed to parse column visibility settings:", error);
          return workUnitsTableHeaders.reduce(
            (acc, col) => ({ ...acc, [col.value]: col.value !== "total_cost" }),
            {},
          );
        }
      }
      return workUnitsTableHeaders.reduce(
        (acc, col) => ({ ...acc, [col.value]: col.value !== "total_cost" }),
        {},
      );
    },
  );

  // Reconcile visibleColumns
  useEffect(() => {
    const defaults: Record<string, boolean> = workUnitsTableHeaders.reduce(
      (acc: Record<string, boolean>, col: any) => {
        acc[col.value] = col.value !== "total_cost";
        return acc;
      },
      {},
    );

    let stored: Record<string, boolean> | null = null;
    try {
      const raw = localStorage.getItem("projectListColumnVisibility");
      if (raw) stored = JSON.parse(raw);
    } catch (e) {
      /* ignore */
    }

    const merged: Record<string, boolean> = {};
    let hasAnyVisible = false;
    let needsUpdate = false;

    workUnitsTableHeaders.forEach((col: any) => {
      const key = col.value;
      const currentVal = visibleColumns[key];
      if (typeof currentVal !== "undefined") {
        merged[key] = currentVal;
        if (currentVal) hasAnyVisible = true;
        return;
      }
      const fromStored = stored ? stored[key] : undefined;
      const value =
        typeof fromStored !== "undefined" ? fromStored : defaults[key];
      merged[key] = value;
      if (value) hasAnyVisible = true;
      needsUpdate = true;
    });

    if (
      needsUpdate ||
      Object.keys(visibleColumns).length !== workUnitsTableHeaders.length
    ) {
      if (!hasAnyVisible) Object.assign(merged, defaults);
      setVisibleColumns(merged);
      return;
    }

    const extraKeys = Object.keys(visibleColumns).filter(
      (k) => !workUnitsTableHeaders.some((c: any) => c.value === k),
    );
    if (extraKeys.length > 0) {
      const pruned: Record<string, boolean> = {};
      workUnitsTableHeaders.forEach((c: any) => {
        pruned[c.value] = visibleColumns[c.value];
      });
      if (Object.values(pruned).filter(Boolean).length === 0)
        Object.assign(pruned, defaults);
      setVisibleColumns(pruned);
    }
  }, [workUnitsTableHeaders]);

  const visibleColumnsForList = useMemo(() => {
    const base = { ...visibleColumns } as Record<string, boolean>;
    if (!anyIntegrationActive) {
      base.integration_type = false;
      base.integration_status = false;
    }
    if (!showCost) base.total_cost = false;
    return base;
  }, [visibleColumns, anyIntegrationActive, showCost]);

  // ========================================
  // INITIAL FETCH STUFF
  // ========================================
  useEffect(() => {
    if (project_id) {
      setSelectedProject({
        project_name: "Loading ...",
        project_id: project_id,
      });
    }
  }, [project_id]);

  useEffect(() => {
    if (org_id) {
      fetchPinnedItems();
      fetchOrgSchedule(org_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org_id]);



  useEffect(() => {
    if (project_id && all_project_data && all_project_data.length) {
      const [filterProject = {}] = all_project_data.filter(
        (ele: { project_id: any }) => ele.project_id === project_id,
      );
      setSelectedProject(filterProject);
      setShowTable(false);
    }
  }, [project_id, all_project_data]);

  useEffect(() => {
    return () => {
      setSelectedProject({ project_name: "", project_id: "" });
    };
  }, []);

  const isLocalFilterApplied = statusFilter.length > 0;

  // ========================================
  // EVENT HANDLERS
  // ========================================
  const handleProjectClick = (
    projectData?: any,
    toggleTable: boolean = false,
  ) => {
    dispatch(resetTimelineState());
    dispatch(resetRoadmap());
    setSelectedProject(projectData);
    setShowTable(toggleTable);

    const itemId = projectData.project_id || projectData.id;
    if (itemId) {
      const currentUrl = window.location.pathname + window.location.search;
      const newHistory = [
          { name: "Work Items", url: currentUrl },
      ];
      const historyParam = JSON.stringify(newHistory);

      if (projectData.type === "roadmap") {
        dispatch(setSingleRoadmap({} as any));
        navigate(`/roadmaps/${itemId}?history=${encodeURIComponent(historyParam)}`);
      } else {
        navigate(`/projects/${itemId}?history=${encodeURIComponent(historyParam)}`);
      }
    } else {
      navigate(`/projects`);
    }
  };

  const handleLoadMore = () => {
    fetchMoreRoadmapsAndProjects();
  };

  // ========================================
  // LAYOUT
  // ========================================
  /* LAYOUT */
  const containerWidth = !isOpenOnSmallScreens
    ? "calc(100vw - 255px)"
    : "calc(100vw - 65px)";

  const containerRef = useRef<HTMLDivElement>(null);

  // Persist isScrolledView state
  const [isScrolledView, setScrolledView] = useState(() => {
    const saved = localStorage.getItem("isScrolledView");
    return saved === "true";
  });

  const handleSetScrolledView = (value: boolean) => {
    setScrolledView(value);
    localStorage.setItem("isScrolledView", String(value));
  };

  // Outside content wheel listener (excluding list/grid):
  // toggle mode only when outer container hits a scroll boundary.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onWheel = (e: WheelEvent) => {
      if (e.deltaY === 0) return;
      const target = e.target as Node;
      const tableEl = tableScrollRef.current;
      const gridEl = gridContainerRef.current;
      const inTableOrGrid =
        (tableEl && tableEl.contains(target)) ||
        (gridEl && gridEl.contains(target));
      if (inTableOrGrid) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceToBottom = scrollHeight - scrollTop - clientHeight;
      const atTop = scrollTop <= 135;
      const atBottom = distanceToBottom <= 135;

      if (e.deltaY < 0 && atTop) {
        handleSetScrolledView(false);
      } else if (e.deltaY > 0 && atBottom) {
        handleSetScrolledView(true);
      }
    };

    container.addEventListener("wheel", onWheel, { passive: true });
    return () => container.removeEventListener("wheel", onWheel);
  }, []);

  // Header wheel listener: handles scroll on compact filters (outside containerRef)
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    const onWheel = (e: WheelEvent) => {
      if (e.deltaY === 0) return;
      if (e.deltaY > 0) {
        handleSetScrolledView(true);
      } else if (e.deltaY < 0) {
        handleSetScrolledView(false);
      }
    };

    header.addEventListener("wheel", onWheel, { passive: true });
    return () => header.removeEventListener("wheel", onWheel);
  }, []);

  const computedHasMore = hasMore;

  // Continuous flag: true during the entire auto-load cycle (stays true between API calls)
  const isAutoLoading = !!(statusFilter && statusFilter.length > 0 && hasMore && filteredProjectData.length < 20);


  return (
    <>
      <OnboardingProjects />

      <div
        style={{ width: containerWidth }}
        className="w-full h-screen flex flex-col bg-tertiary-light dark:bg-tertiary-dark relative "
        data-onboarding-id="work-item-page"
      >
        {/* HEADER */}
        <header ref={headerRef} className="flex-shrink-0 z-40 bg-light-header dark:bg-secondary-dark w-full border-b border-light-border-default dark:border-gray-700">
          {/* Main header row */}
          <div className="px-6 py-4 flex items-center justify-between">
            <div
              className="flex items-center"
              data-onboarding-id="work-item-header"
            >
              <div className="flex flex-col">
                {/* Title */}
                <div className="flex items-center gap-2">
                  <AnimatePresence>
                    {isScrolledView && (
                      <motion.button
                        initial={{ opacity: 0, x: -20, width: 0 }}
                        animate={{ opacity: 1, x: 0, width: "auto" }}
                        exit={{ opacity: 0, x: -20, width: 0 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="smv-sub-header-back-btn mr-2 overflow-hidden flex items-center justify-center"
                        onClick={() => handleSetScrolledView(false)}
                        title="Go Back"
                      >
                        <HiArrowLeft className="text-xl text-light-text-secondary dark:text-gray-300 flex-shrink-0" />
                      </motion.button>
                    )}
                  </AnimatePresence>
                  <motion.h1
                    layout
                    className="font-bold text-light-text-primary dark:text-white leading-tight text-xl flex items-center"
                  >
                    Work Items
                    <div className="flex items-center gap-2 ml-2">
                      <span className="text-sm font-normal text-gray-400 dark:text-gray-500 hidden sm:inline-block">
                        /
                      </span>
                      <div className="flex items-center gap-1">
                        <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                          Epics & Features
                        </span>
                        <CustomTooltip
                          title="This view focuses on high-level planning (Epics & Features). To manage granular tasks, bugs, spikes, and stories, please navigate to a specific epic or feature."
                          arrow
                          placement="right"
                        >
                          <span className="cursor-help flex items-center justify-center">
                            <HiInformationCircle className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" />
                          </span>
                        </CustomTooltip>
                      </div>
                    </div>
                  </motion.h1>
                </div>

                {/* Description */}
                {!isScrolledView && (
                  <p className="text-sm text-light-text-secondary dark:text-slate-400 mt-0.5">
                    Create, track, and visualize your work items for effective
                    project planning.
                  </p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              {(!loading || !orgLoading) && (
                <AddProject
                  isVisible={showModal}
                  selectedProject={selectedProject}
                  handleProjectClick={handleProjectClick}
                  setShowModal={setShowModal}
                />
              )}
            </div>
          </div>

          {/* Header filters */}
          {isScrolledView && (
            <div className="bg-secondary-light/30 dark:bg-transparent border-t border-light-border-subtle dark:border-gray-700/30 px-6 py-3">
              <WorkUnitsFilters
                viewType={viewType}
                setViewType={setViewType}
                visibleColumns={visibleColumns}
                setVisibleColumns={setVisibleColumns}
                projectData={all_project_data}
                onFilteredDataChange={() => {}}
                totalItems={totalItems}
                currentPage={currentPage}
                totalPages={totalPages}
                loading={orgLoading || initialLoading}
                tableScrollRef={tableScrollRef}
                gridScrollRef={gridScrollRef}
                isCompact={false}
                searchType={searchType}
                setSearchType={setSearchType}
                sortBy={sortBy}
                setSortBy={setSortBy}
                sourceFilter={sourceFilter}
                setSourceFilter={setSourceFilter}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                pinnedFilter={pinnedFilter}
                setPinnedFilter={setPinnedFilter}
                quarterFilter={quarterFilter}
                setQuarterFilter={setQuarterFilter}
                loadingMore={loadingMore || isAutoLoading}
                hasActiveFilters={hasActiveFilters}
                onClearFilters={handleClearFilters}
              />
            </div>
          )}
        </header>

        {/* SCROLLABLE CONTENT */}
        <div
          className={`work-list-container flex-1 overflow-y-auto relative bg-tertiary-light dark:bg-tertiary-dark min-h-[300px] no-scrollbar `}
          ref={containerRef}
        >
          {/* Recent Projects */}
          <section
            className={isScrolledView ? "min-h-[10px]" : "min-h-[230px]"}
          >
            {showTable && !isScrolledView && (
              <RecentWorkUnits
                handleProjectClick={handleProjectClick}
                loading={recentProjectsLoading || recentInitialLoading}
              />
            )}
          </section>

          {/* All Work Items Section */}
          {showTable && (
            <ListWorkUnits
              showTable={showTable}
              scrollProgress={0}
              originalFilterOpacity={1}
              viewType={viewType}
              setViewType={setViewType}
              visibleColumns={visibleColumns}
              setVisibleColumns={setVisibleColumns}
              projectData={all_project_data}
              onFilteredDataChange={() => {}}
              totalItems={totalItems}
              currentPage={currentPage}
              totalPages={totalPages}
              loading={orgLoading}
              initialLoading={initialLoading}
              loadingMore={loadingMore}
              onLoadMore={handleLoadMore}
              hasMore={computedHasMore}
              searchType={searchType}
              setSearchType={setSearchType}
              sortBy={sortBy}
              setSortBy={setSortBy}
              sourceFilter={sourceFilter}
              setSourceFilter={setSourceFilter}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              pinnedFilter={pinnedFilter}
              setPinnedFilter={setPinnedFilter}
              quarterFilter={quarterFilter}
              setQuarterFilter={setQuarterFilter}
              filteredProjectData={filteredProjectData}
              handleProjectClick={handleProjectClick}
              visibleColumnsForList={visibleColumnsForList}
              isLocalFilterApplied={isLocalFilterApplied}
              tableScrollRef={tableScrollRef}
              gridScrollRef={gridScrollRef}
              gridContainerRef={gridContainerRef}
              filterSectionRef={filterSectionRef}
              filterVisibility={!isScrolledView}
              hasActiveFilters={hasActiveFilters}
              onClearFilters={handleClearFilters}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default Projects;
